import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { SERVICE_FLOWS, getServiceFlow, ServiceFlow, FormField, ServiceStep } from '../components/rfq/serviceFlowConfig';
import { ArrowLeft, ArrowRight, Send, CheckCircle, FileText, Upload, Shield, Clock, ChevronRight } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface ContactInfo {
  projectName: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  preferredResponseTime: string;
  fileUpload: File | null;
}

// ─── SESSION STORAGE ─────────────────────────────────────────────────────────

const STORAGE_KEY = 'energipro-rfq-progress';

function saveProgress(data: { service: string; formData: Record<string, any>; contact: ContactInfo; currentStep: number }) {
  try {
    const serializable = { ...data, contact: { ...data.contact, fileUpload: null } };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  } catch {}
}

function loadProgress(): { service: string; formData: Record<string, any>; contact: ContactInfo; currentStep: number } | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function clearProgress() {
  sessionStorage.removeItem(STORAGE_KEY);
}

// ─── QUOTE REFERENCE GENERATOR ───────────────────────────────────────────────

function generateQuoteRef(serviceId: string): string {
  const prefix = serviceId.split('-').map(w => w[0]?.toUpperCase()).join('').slice(0, 3);
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `EPG-${prefix}-${timestamp}-${random}`;
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export const RequestQuote = () => {
  const [selectedService, setSelectedService] = useState<string>('');
  const [currentStep, setCurrentStep] = useState(0); // 0 = service select
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    projectName: '', companyName: '', contactName: '',
    email: '', phone: '', preferredResponseTime: '48hrs', fileUpload: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quoteRef, setQuoteRef] = useState('');
  const [showResumeBanner, setShowResumeBanner] = useState(false);

  // Load saved progress on mount
  useEffect(() => {
    const saved = loadProgress();
    if (saved && saved.service) {
      setShowResumeBanner(true);
    }
  }, []);

  const resumeProgress = () => {
    const saved = loadProgress();
    if (saved) {
      setSelectedService(saved.service);
      setFormData(saved.formData);
      setContactInfo(saved.contact);
      setCurrentStep(saved.currentStep);
      setShowResumeBanner(false);
    }
  };

  const dismissResume = () => {
    clearProgress();
    setShowResumeBanner(false);
  };

  // Auto-save progress
  useEffect(() => {
    if (selectedService) {
      saveProgress({ service: selectedService, formData, contact: contactInfo, currentStep });
    }
  }, [selectedService, formData, contactInfo, currentStep]);

  const serviceFlow = useMemo(() => getServiceFlow(selectedService), [selectedService]);
  const totalSteps = serviceFlow ? serviceFlow.steps.length + 2 : 1; // service select + dynamic steps + contact/submit

  // Field update
  const updateField = useCallback((fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  const updateContact = useCallback((field: keyof ContactInfo, value: any) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  }, []);

  // Select service
  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    setFormData({});
    setCurrentStep(1);
  };

  // Navigation
  const goNext = () => {
    if (currentStep < totalSteps - 1) setCurrentStep(prev => prev + 1);
  };
  const goBack = () => {
    if (currentStep === 1) {
      setSelectedService('');
      setCurrentStep(0);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    const ref = generateQuoteRef(selectedService);
    setQuoteRef(ref);

    try {
      // Determine priority based on response time
      const priorityMap: Record<string, string> = {
        '24hrs': 'Emergency',
        '48hrs': 'Express',
        '1week': 'Standard',
      };
      const priority = priorityMap[contactInfo.preferredResponseTime] || 'Standard';

      // Build rfqData from formData + contact info for backend
      const rfqData: Record<string, any> = {
        ...formData,
        service: serviceFlow?.label || selectedService,
        email: contactInfo.email,
        fullName: contactInfo.contactName,
        companyName: contactInfo.companyName,
        phone: contactInfo.phone,
        country: formData.country || formData.location || formData.region || '',
        timeline: contactInfo.preferredResponseTime,
        additionalNotes: formData.additionalNotes || formData.notes || '',
      };

      const payload = {
        rfqId: ref,
        clientName: contactInfo.contactName,
        company: contactInfo.companyName,
        serviceType: serviceFlow?.label || selectedService,
        country: rfqData.country,
        date: new Date().toISOString(),
        priority,
        preferredResponseTime: contactInfo.preferredResponseTime,
        projectName: contactInfo.projectName,
        rfqData,
        formSummary: liveSummary,
      };

      const res = await fetch(`${BACKEND_URL}/api/public/rfq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (res.ok && result.success) {
        // Use the server-issued reference ID if available
        if (result.rfqId) setQuoteRef(result.rfqId);
        clearProgress();
        setIsSubmitted(true);
      } else {
        console.error('RFQ submission failed:', result.error);
        alert('Submission failed. Please try again or contact us directly.');
      }
    } catch (err) {
      console.error('RFQ submission error:', err);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if current step is valid
  const isStepValid = (): boolean => {
    if (currentStep === 0) return selectedService !== '';
    if (!serviceFlow) return false;
    if (currentStep <= serviceFlow.steps.length) {
      // Check required fields in current step
      const step = serviceFlow.steps[currentStep - 1];
      return step.fields.every(field => {
        if (!field.required) return true;
        if (field.conditionalOn) {
          const condValue = formData[field.conditionalOn.field];
          if (condValue !== field.conditionalOn.value) return true; // skip validation if condition not met
        }
        const val = formData[field.id];
        if (field.type === 'multi-select') return Array.isArray(val) && val.length > 0;
        return val !== undefined && val !== '' && val !== null;
      });
    }
    // Contact step
    return contactInfo.contactName !== '' && contactInfo.email !== '' && contactInfo.companyName !== '';
  };

  // ─── LIVE SUMMARY PANEL ─────────────────────────────────────────────────────

  const liveSummary = useMemo(() => {
    if (!serviceFlow) return [];
    const items: { label: string; value: string }[] = [];
    items.push({ label: 'Service', value: serviceFlow.label });

    serviceFlow.steps.forEach(step => {
      step.fields.forEach(field => {
        const val = formData[field.id];
        if (!val || (Array.isArray(val) && val.length === 0)) return;
        if (field.conditionalOn) {
          const condVal = formData[field.conditionalOn.field];
          if (condVal !== field.conditionalOn.value) return;
        }
        let displayVal = val;
        if (Array.isArray(val) && field.options) {
          displayVal = val.map((v: string) => field.options?.find(o => o.id === v)?.label || v).join(', ');
        } else if (field.options) {
          displayVal = field.options.find(o => o.id === val)?.label || val;
        }
        if (field.unit) displayVal = `${displayVal} ${field.unit}`;
        items.push({ label: field.label, value: String(displayVal) });
      });
    });

    return items;
  }, [serviceFlow, formData]);

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  if (isSubmitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-slate-50 pt-32 pb-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-lg p-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-3">Quote Request Submitted</h1>
              <p className="text-slate-600 mb-6">Your request has been received and will be reviewed by our engineering team.</p>
              <div className="bg-slate-50 rounded-lg p-4 mb-8 inline-block">
                <p className="text-sm text-slate-500 mb-1">Quote Reference Number</p>
                <p className="text-xl font-mono font-bold text-orange-600">{quoteRef}</p>
              </div>
              <p className="text-sm text-slate-500 mb-6">A confirmation email has been sent. Our team will respond within your requested timeframe.</p>
              <button
                onClick={() => { setIsSubmitted(false); setSelectedService(''); setCurrentStep(0); setFormData({}); }}
                className="bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-700 transition"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SeoHead
        title="Request Technical Proposal | Smart RFQ System"
        description="Submit a request for technical proposal or RFQ. 15 service-specific flows with dynamic engineering fields, automated validation, and real-time response panels."
        canonicalPath="/request-quote"
      />
      <div className="min-h-screen bg-slate-50 pt-32 pb-16">
        {/* Resume Banner */}
        {showResumeBanner && (
          <div className="max-w-7xl mx-auto px-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-blue-800 font-medium">You have a saved quote in progress.</span>
              </div>
              <div className="flex gap-3">
                <button onClick={resumeProgress} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">
                  Resume
                </button>
                <button onClick={dismissResume} className="text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 transition">
                  Start Fresh
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Request a Quote</h1>
            <p className="text-slate-600 text-lg">Select your service and configure your technical requirements</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Form Area */}
            <div className="flex-1 min-w-0">
              {/* Progress Bar */}
              {selectedService && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">
                      Step {currentStep} of {totalSteps - 1}
                    </span>
                    <span className="text-sm text-slate-500">
                      {currentStep === 0 ? 'Service Selection' :
                       currentStep <= (serviceFlow?.steps.length || 0) ? serviceFlow?.steps[currentStep - 1]?.title :
                       'Contact & Submit'}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(currentStep / (totalSteps - 1)) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Form Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                {/* Step 0: Service Selection */}
                {currentStep === 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Select Service Category</h2>
                    <p className="text-slate-600 mb-6">Choose the service line for your quote request</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {SERVICE_FLOWS.map(flow => (
                        <button
                          key={flow.id}
                          onClick={() => handleServiceSelect(flow.id)}
                          className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                            selectedService === flow.id
                              ? 'border-orange-500 bg-orange-50 shadow-md'
                              : 'border-slate-200 hover:border-orange-300'
                          }`}
                        >
                          <span className="text-2xl mb-2 block">{flow.icon}</span>
                          <span className="font-semibold text-slate-900 text-sm">{flow.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dynamic Service Steps */}
                {currentStep > 0 && currentStep <= (serviceFlow?.steps.length || 0) && serviceFlow && (
                  <DynamicStep
                    step={serviceFlow.steps[currentStep - 1]}
                    formData={formData}
                    updateField={updateField}
                  />
                )}

                {/* Contact & Submit Step */}
                {currentStep > (serviceFlow?.steps.length || 0) && serviceFlow && (
                  <ContactStep
                    contactInfo={contactInfo}
                    updateContact={updateContact}
                  />
                )}

                {/* Navigation */}
                {currentStep > 0 && (
                  <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                    <button
                      onClick={goBack}
                      className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>

                    {currentStep < totalSteps - 1 ? (
                      <button
                        onClick={goNext}
                        disabled={!isStepValid()}
                        className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        Next <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={!isStepValid() || isSubmitting}
                        className="flex items-center gap-2 px-8 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                            Submitting...
                          </>
                        ) : (
                          <>Submit Quote Request <Send className="w-4 h-4" /></>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Live Output Panel */}
            {selectedService && (
              <div className="lg:w-80 xl:w-96 shrink-0">
                <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-28">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-orange-600" />
                    <h3 className="font-bold text-slate-900">Live Quote Summary</h3>
                  </div>
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    {liveSummary.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">Make selections to see your quote summary</p>
                    ) : (
                      liveSummary.map((item, idx) => (
                        <div key={idx} className="border-b border-slate-100 pb-2 last:border-0">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">{item.label}</p>
                          <p className="text-sm font-medium text-slate-800">{item.value}</p>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Trust Indicators */}
                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                      <Shield className="w-3.5 h-3.5 text-green-600" />
                      <span>Secure submission • Data encrypted</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5 text-blue-600" />
                      <span>Progress auto-saved</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

// ─── DYNAMIC STEP RENDERER ───────────────────────────────────────────────────

function DynamicStep({ step, formData, updateField }: {
  step: ServiceStep;
  formData: Record<string, any>;
  updateField: (id: string, value: any) => void;
}) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-1">{step.title}</h2>
      {step.description && <p className="text-slate-600 mb-6">{step.description}</p>}
      {!step.description && <div className="mb-6" />}
      <div className="space-y-5">
        {step.fields.map(field => (
          <FieldRenderer key={field.id} field={field} formData={formData} updateField={updateField} />
        ))}
      </div>
    </div>
  );
}

// ─── FIELD RENDERER ─────────────────────────────────────────────────────────

function FieldRenderer({ field, formData, updateField }: {
  field: FormField;
  formData: Record<string, any>;
  updateField: (id: string, value: any) => void;
}) {
  // Check conditional visibility
  if (field.conditionalOn) {
    const condValue = formData[field.conditionalOn.field];
    if (condValue !== field.conditionalOn.value) return null;
  }

  const value = formData[field.id];

  switch (field.type) {
    case 'select':
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value || ''}
            onChange={e => updateField(field.id, e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
          {field.helpText && <p className="text-xs text-slate-500 mt-1">{field.helpText}</p>}
        </div>
      );

    case 'multi-select':
      const selectedValues: string[] = Array.isArray(value) ? value : [];
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {field.options?.map(opt => {
              const isSelected = selectedValues.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    const newVal = isSelected
                      ? selectedValues.filter(v => v !== opt.id)
                      : [...selectedValues, opt.id];
                    updateField(field.id, newVal);
                  }}
                  className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-all ${
                    isSelected
                      ? 'border-orange-500 bg-orange-50 text-orange-800 font-medium'
                      : 'border-slate-200 text-slate-700 hover:border-orange-300'
                  }`}
                >
                  <span className={`inline-block w-4 h-4 rounded border mr-2 align-middle ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-slate-300'}`}>
                    {isSelected && <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </span>
                  {opt.label}
                </button>
              );
            })}
          </div>
          {field.helpText && <p className="text-xs text-slate-500 mt-1">{field.helpText}</p>}
        </div>
      );

    case 'radio':
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <div className="flex gap-3 flex-wrap">
            {field.options?.map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => updateField(field.id, opt.id)}
                className={`px-5 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                  value === opt.id
                    ? 'border-orange-500 bg-orange-50 text-orange-800'
                    : 'border-slate-200 text-slate-700 hover:border-orange-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {field.helpText && <p className="text-xs text-slate-500 mt-1">{field.helpText}</p>}
        </div>
      );

    case 'number':
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {field.label} {field.required && <span className="text-red-500">*</span>}
            {field.unit && <span className="text-slate-400 font-normal"> ({field.unit})</span>}
          </label>
          <input
            type="number"
            value={value || ''}
            onChange={e => updateField(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
          />
          {field.helpText && <p className="text-xs text-slate-500 mt-1">{field.helpText}</p>}
        </div>
      );

    case 'text':
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={value || ''}
            onChange={e => updateField(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
          />
          {field.helpText && <p className="text-xs text-slate-500 mt-1">{field.helpText}</p>}
        </div>
      );

    case 'file':
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {field.label}
          </label>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-400 transition cursor-pointer">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Click or drag file to upload</p>
            <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLS, DWG up to 25MB</p>
          </div>
        </div>
      );

    default:
      return null;
  }
}

// ─── CONTACT STEP ───────────────────────────────────────────────────────────

function ContactStep({ contactInfo, updateContact }: {
  contactInfo: ContactInfo;
  updateContact: (field: keyof ContactInfo, value: any) => void;
}) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Contact & Submit</h2>
      <p className="text-slate-600 mb-6">Provide your contact details to complete the quote request</p>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Project Name <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={contactInfo.projectName}
            onChange={e => updateContact('projectName', e.target.value)}
            placeholder="e.g., Well Testing Campaign - Field X"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={contactInfo.companyName}
            onChange={e => updateContact('companyName', e.target.value)}
            placeholder="Your company name"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Contact Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={contactInfo.contactName}
              onChange={e => updateContact('contactName', e.target.value)}
              placeholder="Full name"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Phone
            </label>
            <input
              type="tel"
              value={contactInfo.phone}
              onChange={e => updateContact('phone', e.target.value)}
              placeholder="+966..."
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={contactInfo.email}
            onChange={e => updateContact('email', e.target.value)}
            placeholder="your@company.com"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Preferred Response Time
          </label>
          <div className="flex gap-3 flex-wrap">
            {[
              { id: '24hrs', label: '24 Hours' },
              { id: '48hrs', label: '48 Hours' },
              { id: '1week', label: '1 Week' },
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => updateContact('preferredResponseTime', opt.id)}
                className={`px-5 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                  contactInfo.preferredResponseTime === opt.id
                    ? 'border-orange-500 bg-orange-50 text-orange-800'
                    : 'border-slate-200 text-slate-700 hover:border-orange-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            File Upload <span className="text-slate-400 font-normal">(optional — drawings, specs, RFQ docs)</span>
          </label>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-400 transition cursor-pointer">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Click or drag files to upload</p>
            <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLS, DWG up to 25MB</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RequestQuote;
